import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

function BookingModal({ type, onClose, prefillDate, eventName }) {
  const isVIP = type === 'vip';

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    date: prefillDate || '',
    partySize: '',
    budget: '',
    notes: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success
  const [error, setError] = useState('');

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setError('Please enter a valid email address.');
        return;
      }
    }

    const webhookUrl = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
    if (!webhookUrl) {
      setError(
        'Missing REACT_APP_DISCORD_WEBHOOK_URL. Add it to your .env file.'
      );
      return;
    }

    const budgetLabels = {
      '500-1000': '$500 – $1,000',
      '1000-2500': '$1,000 – $2,500',
      '2500-5000': '$2,500 – $5,000',
      '5000+': '$5,000+',
      discuss: "Let's talk",
    };

    const heading = isVIP
      ? '**🍾 New VIP Table Inquiry — NXTLVL**'
      : '**📝 New Guest List Request — NXTLVL**';

    const content = [
      heading,
      `**Type:** ${isVIP ? 'VIP Table' : 'Guest List'}`,
      // Surface the event name when this booking came from a featured event
      eventName ? `**Event:** ${eventName}` : null,
      `**Name:** ${form.name}`,
      `**Phone:** ${form.phone}`,
      form.email ? `**Email:** ${form.email}` : null,
      `**Preferred Date:** ${form.date}`,
      `**Party Size:** ${form.partySize}`,
      isVIP && form.budget
        ? `**Budget:** ${budgetLabels[form.budget] || form.budget}`
        : null,
      form.notes ? `\n**Notes:**\n${form.notes}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    try {
      setStatus('sending');
      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const txt = await res.text();
        throw new Error(txt || `Request failed: ${res.status}`);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to send. Please try again in a moment.');
      setStatus('idle');
    }
  };

  return (
    <div className="nxt-modal-bg" onClick={onClose}>
      <div
        className="nxt-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          className="nxt-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <span aria-hidden="true">×</span>
        </button>

        {status === 'success' ? (
          <div className="nxt-modal-success">
            <p className="nxt-modal-eyebrow">
              <span className="nxt-pulse" /> CONFIRMED
            </p>
            <h2 className="nxt-modal-title">You&rsquo;re in.</h2>
            <p className="nxt-modal-body">
              We got it. Expect a confirmation for your{' '}
              {isVIP ? 'table' : 'guest list spot'} shortly.
            </p>
            <button
              className="nxt-btn nxt-btn--primary nxt-btn--block"
              onClick={onClose}
            >
              <span className="nxt-btn-text">Close</span>
            </button>
          </div>
        ) : (
          <>
            <p className="nxt-modal-eyebrow">
              {isVIP ? 'VIP RESERVATION' : 'GUEST LIST'}
              {eventName && (
                <>
                  <span className="nxt-modal-eyebrow-sep">·</span>
                  <span className="nxt-modal-eyebrow-event">{eventName}</span>
                </>
              )}
            </p>
            <h2 className="nxt-modal-title">
              {isVIP ? 'Reserve your table.' : 'Join the list.'}
            </h2>
            <p className="nxt-modal-body">
              {isVIP
                ? "A few details — we'll come back with availability and pricing within the hour."
                : "Quick details — we'll send confirmation before the night."}
            </p>

            <form className="nxt-form" onSubmit={handleSubmit} noValidate>
              <div className="nxt-field">
                <label htmlFor="bm-name">Name</label>
                <input
                  id="bm-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="nxt-field-row">
                <div className="nxt-field">
                  <label htmlFor="bm-phone">Phone</label>
                  <input
                    id="bm-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                    required
                  />
                </div>
                <div className="nxt-field">
                  <label htmlFor="bm-email">
                    Email
                    {!isVIP && (
                      <span className="nxt-field-meta">optional</span>
                    )}
                  </label>
                  <input
                    id="bm-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required={isVIP}
                  />
                </div>
              </div>

              <div className="nxt-field-row">
                <div className="nxt-field">
                  <label htmlFor="bm-date">Date</label>
                  <input
                    id="bm-date"
                    name="date"
                    type="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="nxt-field">
                  <label htmlFor="bm-party">Party Size</label>
                  <input
                    id="bm-party"
                    name="partySize"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="50"
                    value={form.partySize}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {isVIP && (
                <div className="nxt-field">
                  <label htmlFor="bm-budget">Budget</label>
                  <select
                    id="bm-budget"
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select a range
                    </option>
                    <option value="500-1000">$500 – $1,000</option>
                    <option value="1000-2500">$1,000 – $2,500</option>
                    <option value="2500-5000">$2,500 – $5,000</option>
                    <option value="5000+">$5,000+</option>
                    <option value="discuss">Let&rsquo;s talk</option>
                  </select>
                </div>
              )}

              <div className="nxt-field">
                <label htmlFor="bm-notes">
                  Notes <span className="nxt-field-meta">optional</span>
                </label>
                <textarea
                  id="bm-notes"
                  name="notes"
                  rows="3"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder={
                    isVIP
                      ? 'Occasion, music preference, etc.'
                      : 'Anything we should know?'
                  }
                />
              </div>

              {error && <p className="nxt-form-error">{error}</p>}

              <button
                type="submit"
                className="nxt-btn nxt-btn--primary nxt-btn--block"
                disabled={status === 'sending'}
              >
                <span className="nxt-btn-text">
                  {status === 'sending'
                    ? 'Sending…'
                    : isVIP
                    ? 'Request Reservation'
                    : 'Join Guest List'}
                </span>
                {status !== 'sending' && (
                  <span className="nxt-btn-arrow" aria-hidden="true">→</span>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

BookingModal.propTypes = {
  type: PropTypes.oneOf(['vip', 'guestlist']).isRequired,
  onClose: PropTypes.func.isRequired,
  prefillDate: PropTypes.string,
  eventName: PropTypes.string,
};

BookingModal.defaultProps = {
  prefillDate: '',
  eventName: '',
};

export default BookingModal;